import { supabase } from './supabase';
import { startOfWeek, format } from 'date-fns';
import type {
  IDateRange,
  IDashboardOverviewStats,
  IFormActivityRow,
  IFormWithCount,
  IPerformanceDataPoint,
  IFunnelStage,
  ILeadSourceRow,
  ISyncActivityEntry,
} from '~/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyDateFilter<T extends { gte: any; lte: any }>(query: T, range: IDateRange): T {
  if (range.startDate) {
    query = query.gte('created_at', range.startDate.toISOString());
  }
  if (range.endDate) {
    query = query.lte('created_at', range.endDate.toISOString());
  }
  return query;
}

export async function fetchOverviewStats(range: IDateRange): Promise<IDashboardOverviewStats> {
  const { count: meetingsBooked } = await applyDateFilter(
    supabase
      .from('form_submissions')
      .select('*', { count: 'exact', head: true })
      .eq('calendly_event_booked', true)
      .is('deleted_at', null),
    range,
  );

  const { count: enrichedLeads } = await applyDateFilter(
    supabase
      .from('form_submissions')
      .select('*', { count: 'exact', head: true })
      .eq('submitted_to_hubspot', true)
      .is('deleted_at', null),
    range,
  );

  const { data: contactRows } = await applyDateFilter(
    supabase
      .from('form_submissions')
      .select('hubspot_contact_id')
      .not('hubspot_contact_id', 'is', null)
      .is('deleted_at', null),
    range,
  );
  const contactsCreated = new Set(contactRows?.map((r) => r.hubspot_contact_id)).size;

  const { data: leadRows } = await applyDateFilter(
    supabase.from('form_submissions').select('email').is('deleted_at', null),
    range,
  );
  const leadsCaptured = new Set(leadRows?.map((r) => r.email)).size;

  return {
    meetingsBooked: meetingsBooked ?? 0,
    enrichedLeads: enrichedLeads ?? 0,
    contactsCreated,
    leadsCaptured,
  };
}

const PAGE_SIZE = 25;

export async function fetchFormActivity(
  range: IDateRange,
  formId?: string,
  page = 1,
): Promise<{ rows: IFormActivityRow[]; total: number }> {
  let query = supabase
    .from('form_submissions')
    .select('*', { count: 'exact' })
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

  query = applyDateFilter(query, range);
  if (formId) {
    query = query.eq('form_id', formId);
  }

  const { data, count } = await query;

  // Fetch sync log entries for these submissions across all integrations
  const submissionIds = (data ?? []).map((r) => r.id);
  /* Map: integration_id -> provider name */
  const integrationProviderMap = new Map<string, string>();
  /* Map: submission_id -> provider -> { status, errorMessage } */
  const syncLogMap = new Map<string, Map<string, { status: string; errorMessage: string | null }>>();

  if (submissionIds.length > 0) {
    const { data: integrations } = await supabase.from('integrations').select('id, provider');
    for (const i of integrations ?? []) {
      integrationProviderMap.set(i.id, i.provider);
    }

    const { data: syncLogs } = await supabase
      .from('integration_sync_log')
      .select('submission_id, integration_id, status, error_message')
      .in('submission_id', submissionIds)
      .order('created_at', { ascending: false });

    for (const log of syncLogs ?? []) {
      const provider = integrationProviderMap.get(log.integration_id);
      if (!provider) continue;
      if (!syncLogMap.has(log.submission_id)) {
        syncLogMap.set(log.submission_id, new Map());
      }
      const providerMap = syncLogMap.get(log.submission_id)!;
      if (!providerMap.has(provider)) {
        providerMap.set(provider, { status: log.status, errorMessage: log.error_message });
      }
    }
  }

  const rows: IFormActivityRow[] = (data ?? []).map((row) => {
    const providerSync = syncLogMap.get(row.id);
    const hubspot = providerSync?.get('hubspot');
    const attio = providerSync?.get('attio');
    const slack = providerSync?.get('slack');
    return {
      id: row.id,
      createdAt: row.created_at,
      status: row.is_complete ? 'Submitted' : 'Partial',
      qualification: row.hubspot_contact_id ? 'Qualified' : null,
      email: row.email ?? '',
      formData: (row.form_data as Record<string, unknown>) ?? {},
      calendlyEventBooked: row.calendly_event_booked ?? false,
      hubspotContactId: row.hubspot_contact_id ?? null,
      hubspotContactUrl: row.hubspot_contact_url ?? null,
      hubspotSyncStatus: (hubspot?.status as IFormActivityRow['hubspotSyncStatus']) ?? null,
      hubspotSyncError: hubspot?.errorMessage ?? null,
      attioPersonUrl: row.attio_person_url ?? null,
      attioSyncStatus: (attio?.status as IFormActivityRow['attioSyncStatus']) ?? null,
      attioSyncError: attio?.errorMessage ?? null,
      slackSyncStatus: (slack?.status as IFormActivityRow['slackSyncStatus']) ?? null,
      slackSyncError: slack?.errorMessage ?? null,
    };
  });

  return { rows, total: count ?? 0 };
}

export async function fetchEnabledIntegrations(formId: string): Promise<string[]> {
  const { data } = await supabase
    .from('form_integrations')
    .select('integrations!inner(provider)')
    .eq('form_id', formId)
    .eq('enabled', true);

  return (data ?? []).map((fi) => (fi.integrations as unknown as { provider: string }).provider);
}

export async function fetchFormsWithCounts(): Promise<IFormWithCount[]> {
  const { data, error } = await supabase
    .from('forms')
    .select('*, form_submissions(count)')
    .order('updated_at', { ascending: false });

  if (error) throw error;

  return (data ?? []).map((form) => ({
    ...form,
    submissionCount: (form.form_submissions as Array<{ count: number }>)?.[0]?.count ?? 0,
  }));
}

export async function fetchWeeklyPerformance(range: IDateRange): Promise<IPerformanceDataPoint[]> {
  const { data } = await applyDateFilter(
    supabase
      .from('form_submissions')
      .select('created_at, is_complete')
      .is('deleted_at', null)
      .order('created_at', { ascending: true }),
    range,
  );

  const weekMap = new Map<string, { partial: number; completed: number }>();

  for (const row of data ?? []) {
    const weekStart = startOfWeek(new Date(row.created_at), { weekStartsOn: 1 });
    const key = format(weekStart, 'MMM d');
    const entry = weekMap.get(key) ?? { partial: 0, completed: 0 };
    if (row.is_complete) {
      entry.completed++;
    } else {
      entry.partial++;
    }
    weekMap.set(key, entry);
  }

  return Array.from(weekMap.entries()).map(([week, counts]) => ({
    week,
    partialResponses: counts.partial,
    completedResponses: counts.completed,
  }));
}

export async function fetchFunnelData(range: IDateRange): Promise<IFunnelStage[]> {
  const { count: totalCount } = await applyDateFilter(
    supabase.from('form_submissions').select('*', { count: 'exact', head: true }).is('deleted_at', null),
    range,
  );
  const total = totalCount ?? 0;

  const { count: completedCount } = await applyDateFilter(
    supabase
      .from('form_submissions')
      .select('*', { count: 'exact', head: true })
      .eq('is_complete', true)
      .is('deleted_at', null),
    range,
  );
  const completed = completedCount ?? 0;

  const { count: qualifiedCount } = await applyDateFilter(
    supabase
      .from('form_submissions')
      .select('*', { count: 'exact', head: true })
      .not('hubspot_contact_id', 'is', null)
      .is('deleted_at', null),
    range,
  );
  const qualified = qualifiedCount ?? 0;

  const { count: meetingsCount } = await applyDateFilter(
    supabase
      .from('form_submissions')
      .select('*', { count: 'exact', head: true })
      .eq('calendly_event_booked', true)
      .is('deleted_at', null),
    range,
  );
  const meetings = meetingsCount ?? 0;

  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

  return [
    { label: 'Form Starts', count: total, percentage: 100 },
    { label: 'Form Completed', count: completed, percentage: pct(completed) },
    { label: 'Qualified', count: qualified, percentage: pct(qualified) },
    { label: 'Meetings Booked', count: meetings, percentage: pct(meetings) },
  ];
}

export async function softDeleteSubmissions(ids: string[]): Promise<void> {
  const { error } = await supabase
    .from('form_submissions')
    .update({ deleted_at: new Date().toISOString() })
    .in('id', ids);
  if (error) throw new Error(error.message);
}

export async function fetchLeadSources(
  range: IDateRange,
  page = 1,
): Promise<{ rows: ILeadSourceRow[]; total: number }> {
  const { data } = await applyDateFilter(
    supabase.from('form_submissions').select('utm_source, is_complete, calendly_event_booked').is('deleted_at', null),
    range,
  );

  const sourceMap = new Map<string, { total: number; partial: number; completed: number; meetings: number }>();

  for (const row of data ?? []) {
    const source = (row.utm_source as string) || 'Direct';
    const entry = sourceMap.get(source) ?? { total: 0, partial: 0, completed: 0, meetings: 0 };
    entry.total++;
    if (row.is_complete) {
      entry.completed++;
    } else {
      entry.partial++;
    }
    if (row.calendly_event_booked) {
      entry.meetings++;
    }
    sourceMap.set(source, entry);
  }

  const allRows: ILeadSourceRow[] = Array.from(sourceMap.entries())
    .map(([source, counts]) => ({
      source,
      totalLeads: counts.total,
      partialSubmissions: counts.partial,
      formsCompleted: counts.completed,
      meetingsBooked: counts.meetings,
    }))
    .sort((a, b) => b.totalLeads - a.totalLeads);

  const start = (page - 1) * PAGE_SIZE;
  return {
    rows: allRows.slice(start, start + PAGE_SIZE),
    total: allRows.length,
  };
}

export async function fetchSyncActivity(formId: string): Promise<ISyncActivityEntry[]> {
  /* Fetch submissions for this form, then their sync log entries */
  const { data: submissions } = await supabase.from('form_submissions').select('id, email').eq('form_id', formId);

  if (!submissions || submissions.length === 0) return [];

  const submissionIds = submissions.map((s) => s.id);
  const emailMap = new Map(submissions.map((s) => [s.id, s.email ?? '']));

  const { data: integrations } = await supabase.from('integrations').select('id, provider');
  const providerMap = new Map((integrations ?? []).map((i) => [i.id, i.provider]));

  const { data: logs } = await supabase
    .from('integration_sync_log')
    .select('id, submission_id, integration_id, sync_type, status, error_message, created_at')
    .in('submission_id', submissionIds)
    .order('created_at', { ascending: false })
    .limit(50);

  return (logs ?? []).map((log) => ({
    id: log.id,
    email: emailMap.get(log.submission_id) ?? '',
    provider: providerMap.get(log.integration_id) ?? 'unknown',
    syncType: log.sync_type,
    status: log.status,
    errorMessage: log.error_message,
    createdAt: log.created_at,
  }));
}
